// Sanity client for the public-facing frontend.
//
// Reads ONLY — no token. The `production` dataset is publicly readable
// (verified during Phase 5 planning), so anonymous access is sufficient.
//
// `useCdn: true` routes through Sanity's CDN edge cache for low-latency
// reads. CDN is eventually-consistent (typically <2s lag behind authoring),
// which is acceptable for editorial content. If we ever need
// strongly-consistent reads (e.g. for an authenticated draft preview
// route), instantiate a second client with `useCdn: false`.
//
// `perspective: 'published'` excludes drafts — drafts only appear when
// authenticated via a token. The public site sees published content only.

import {createClient} from '@sanity/client';

export const sanityClient = createClient({
    projectId: 'jt2sdxjv',
    dataset: 'production',
    apiVersion: '2024-01-01',
    useCdn: true,
    perspective: 'published',
});

// Default timeout for FE Sanity reads (ms). After this, the adapter falls
// back to data.js silently. Tuned conservatively — 3s is long enough to
// tolerate slow networks but short enough that a user on a flaky connection
// gets the static fallback rather than staring at a stale-looking page.
export const SANITY_FETCH_TIMEOUT_MS = 3000;

// Wrap a Sanity client.fetch() in a timeout that resolves to a fallback
// value if the network call hasn't returned in time. Never throws — the
// FE component always sees either the live value or the fallback.
export const fetchWithFallback = (query, params, fallback, timeoutMs = SANITY_FETCH_TIMEOUT_MS) => {
    return Promise.race([
        sanityClient.fetch(query, params),
        new Promise((resolve) => setTimeout(() => resolve(fallback), timeoutMs)),
    ]).catch(() => fallback);
};
